import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import {
  useNewsFilters,
  useNewsListing,
} from 'volto-ministerio-publico-federal/hooks';
import type { QueryItem } from 'volto-ministerio-publico-federal/hooks/useNewsListing';
import NewsForm from '../NewsFolderView/NewsForm';
import NewsContext from '../NewsFolderView/NewsContext';
import {
  MPFButton,
  NewsCardRow,
  NewsHighlightCard,
} from 'volto-ministerio-publico-federal/components/UI';
import type { NewsCardItem } from 'volto-ministerio-publico-federal/components/UI';
import { DestaquesGrid } from '../CapaView/DestaquesGrid';
import { DESTAQUES_COUNT, type NewsItem } from '../CapaView/helpers';
import CollectionText from '../CollectionText';
import { decodeVocabularyToken } from 'volto-ministerio-publico-federal/utils/decodeVocabularyToken';
import {
  getRichTextData,
  hasOpeningText,
  isEyebrowText,
  toPlainText,
} from 'volto-ministerio-publico-federal/components/View/NewsFolderView/openingText';

type Content = Record<string, unknown>;

interface AllNewsProps {
  content: Content;
  hideForm?: boolean;
}

/** Margem para a rolagem infinita disparar antes de o leitor bater no fim. */
const AUTOLOAD_ROOT_MARGIN = '400px';

export const AllNews: React.FC<AllNewsProps> = ({ content, hideForm }) => {
  // `setParameters` mantém o nome de antes por causa do `NewsContext`, mas
  // agora escreve os filtros na URL em vez de em estado local.
  const {
    filters: {
      subject,
      searchable_text,
      sort_order,
      unidade,
      date_start,
      date_end,
    },
    setFilters: setParameters,
  } = useNewsFilters();

  const query = useMemo(() => {
    const q: QueryItem[] = [];

    if (searchable_text) {
      q.push({
        i: 'SearchableText',
        o: 'plone.app.querystring.operation.string.contains',
        v: searchable_text,
      });
    }

    if (subject.length) {
      // As categorias vêm do vocabulário `noticiatemas` (campo `tema` da
      // notícia), não das keywords livres do Plone (`Subject`) — filtrar por
      // `Subject` aqui nunca batia com a categoria escolhida.
      //
      // O decode é rede de segurança para link antigo que ainda carregue o
      // token escapado do vocabulário (`Direitos do Cidad\xe3o`).
      q.push({
        i: 'tema',
        o: 'plone.app.querystring.operation.selection.any',
        v: subject.map(decodeVocabularyToken),
      });
    }

    if (unidade) {
      q.push({
        i: 'unidadesPublicacao',
        o: 'plone.app.querystring.operation.selection.any',
        v: unidade,
      });
    }

    // `effective` é datetime: sem o fim do dia, a data escolhida como "até"
    // vale meia-noite e as notícias daquele mesmo dia ficariam de fora.
    const endOfDay = date_end ? `${date_end} 23:59:59` : '';

    if (date_start && endOfDay) {
      q.push({
        i: 'effective',
        o: 'plone.app.querystring.operation.date.between',
        v: [date_start, endOfDay],
      });
    } else if (date_start) {
      q.push({
        i: 'effective',
        o: 'plone.app.querystring.operation.date.largerThan',
        v: date_start,
      });
    } else if (endOfDay) {
      q.push({
        i: 'effective',
        o: 'plone.app.querystring.operation.date.lessThan',
        v: endOfDay,
      });
    }

    return q;
  }, [searchable_text, subject, unidade, date_start, date_end]);

  const {
    result,
    loading,
    isCollection,
    effectiveCurrentPage,
    hasMore,
    loadingMore,
    loadMore,
  } = useNewsListing(content, {
    query,
    sortOrder: sort_order,
    mode: 'loadMore',
  });

  const hasFilters = Boolean(
    searchable_text || subject.length || unidade || date_start || date_end,
  );

  // Destaques escolhidos a dedo na edição da pasta (behavior
  // `noticias_destaques`). Só na listagem completa: com filtro aplicado, o que
  // o leitor pediu é o recorte dele, não a curadoria da página.
  const highlights = useMemo(() => {
    if (hasFilters) {
      return [];
    }

    const items = content?.noticias_destaques;

    return Array.isArray(items) ? (items as NewsCardItem[]) : [];
  }, [content?.noticias_destaques, hasFilters]);

  // O catálogo não tem operador de exclusão por UID, então o destaque sai da
  // listagem aqui — uma página pode vir com 8 ou 9 cards em vez de 10.
  const rawItems = useMemo(() => {
    const highlighted = new Set(
      highlights.map((item) => flattenToAppURL(item['@id'])),
    );

    // Critério de caminho (`uid::profundidade`) também devolve a própria
    // pasta consultada; ela é o contêiner da lista, não um item dela.
    const rootUids = new Set(
      (Array.isArray(content?.query) ? content.query : [])
        .filter(
          (c: { i?: string; v?: unknown }) =>
            c?.i === 'path' && typeof c.v === 'string',
        )
        .map((c: { v: string }) => c.v.split('::')[0]),
    );

    return (result?.items ?? []).filter(
      (item) =>
        !highlighted.has(flattenToAppURL(item['@id'])) &&
        !rootUids.has((item as { UID?: string }).UID ?? ''),
    );
  }, [result?.items, highlights, content?.query]);

  // Sem destaques curados na página (e sem filtro), a faixa de destaques da
  // capa (`DestaquesGrid`) entra no lugar do bloco de highlights. Para não
  // repetir conteúdo, as primeiras notícias da própria listagem alimentam a
  // grade e saem da lista abaixo — mesma lógica de exclusão dos highlights.
  //
  // Só na listagem de notícias: uma `Collection` genérica lista o que a query
  // dela pedir (documentos, por exemplo), e virar hero da capa não faz sentido.
  const destaquesFallbackActive =
    !isCollection && !hasFilters && highlights.length === 0;

  const destaquesFallback = useMemo(
    () => (destaquesFallbackActive ? rawItems.slice(0, DESTAQUES_COUNT) : []),
    [destaquesFallbackActive, rawItems],
  );

  const items = useMemo(
    () =>
      destaquesFallbackActive ? rawItems.slice(DESTAQUES_COUNT) : rawItems,
    [destaquesFallbackActive, rawItems],
  );

  // A rolagem infinita só entra depois do primeiro "Mostrar mais": carregar
  // sozinho de cara tira do leitor a decisão de continuar na lista.
  const [autoLoad, setAutoLoad] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const filtersKey = `${searchable_text}|${subject.join(',')}|${unidade}|${date_start}|${date_end}|${sort_order}`;
  useEffect(() => {
    setAutoLoad(false);
  }, [filtersKey]);

  useEffect(() => {
    const node = sentinelRef.current;

    if (
      !autoLoad ||
      !hasMore ||
      loadingMore ||
      !node ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          // Desliga antes de pedir: a sentinela continua visível enquanto o
          // lote não chega, e um segundo disparo pularia uma página.
          observer.disconnect();
          loadMore();
        }
      },
      { rootMargin: AUTOLOAD_ROOT_MARGIN },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [autoLoad, hasMore, loadingMore, loadMore]);

  const handleShowMore = () => {
    setAutoLoad(true);
    loadMore();
  };

  const showForm = !hideForm && !isCollection;
  // Texto de abertura curto vira a tarja do cabeçalho ("Sala de imprensa"
  // é só o padrão); texto rico continua indo para o corpo, como antes.
  const openingHtml = getRichTextData(content as { text?: { data?: string } });
  const eyebrowFromText = isEyebrowText(openingHtml)
    ? toPlainText(openingHtml)
    : undefined;
  const hasContentText = hasOpeningText(openingHtml) && !eyebrowFromText;

  // O cabeçalho traz o h1 da página, mas ele só aparece quando não há texto
  // de abertura. Com texto, quem renderiza é o CollectionText — HTML do
  // editor, sem heading garantido — então o h1 vai visualmente oculto.
  const pageTitle = isCollection
    ? (content?.title as string) || 'Notícias'
    : 'Notícias';

  return (
    <NewsContext.Provider
      value={useMemo(
        () => ({
          currentPage: effectiveCurrentPage,
          setParameters,
        }),
        [effectiveCurrentPage, setParameters],
      )}
    >
      <section className="news-folder-view" id="news-folder-view">
        <div className="news-folder-view__inner">
          {!hasContentText && content?.ocultar_titulo && (
            // "Ocultar título" (behavior `ocultar_titulo`, na pasta e na
            // coleção): o cabeçalho inteiro sai da tela — sobraria só o filete
            // vazio — e o `h1` fica para leitores de tela, como no `PageTitle`.
            <h1 className="sr-only">{pageTitle}</h1>
          )}

          {!hasContentText && !content?.ocultar_titulo && (
            // Cabeçalho da página: só o título sobre um filete, como no
            // design. A tarja azul do `NewsHeaderCard` (que continua servindo
            // o `NewsFolderView`) não existe mais aqui.
            <header className="flex items-center gap-4 border-b-2 border-outline px-2 py-2">
              <div>
                {eyebrowFromText && (
                  <p className="mb-0 text-sm font-semibold uppercase tracking-wider text-on-surface-low">
                    {eyebrowFromText}
                  </p>
                )}
                <h1 className="mb-0 text-h2 font-medium text-primary dark:text-night-100">
                  {pageTitle}
                </h1>
                {/* Só descrição escrita pelo editor: o texto genérico que
                    ficava aqui antes era enfeite, e o design não o prevê. */}
                {isCollection && Boolean(content?.description) && (
                  <p className="mb-0 mt-1 max-w-3xl text-base text-on-surface">
                    {content.description as string}
                  </p>
                )}
              </div>
            </header>
          )}

          {highlights.length > 0 ? (
            <div className="grid gap-8 md:grid-cols-2">
              {highlights.map((item, index) => (
                <NewsHighlightCard
                  key={item['@id']}
                  item={item}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              ))}
            </div>
          ) : destaquesFallbackActive ? (
            // Sem highlights curados: a grade de destaques da capa entra no
            // lugar, preenchida com as últimas notícias da própria listagem.
            <DestaquesGrid
              fallback={destaquesFallback as NewsItem[]}
              loading={loading}
            />
          ) : null}

          {showForm && <NewsForm />}

          {hasContentText && (
            <>
              <h1 className="sr-only">{pageTitle}</h1>
              <CollectionText content={content} />
            </>
          )}

          {loading && (
            <div className="flex justify-center py-12">
              <span className="mpf-loading mpf-loading-spinner mpf-loading-lg text-primary dark:text-night-100" />
            </div>
          )}

          {!loading && (
            <>
              {items.length === 0 && (
                <div className="py-12 text-center">
                  <p className="text-gray-500">Nenhum resultado encontrado.</p>
                </div>
              )}

              {/* Os cards são irmãos diretos de propósito: a regra
                  `.mpf-news-card-row-link + .mpf-news-card-row-link` de
                  `_news-card-row.scss` é o que desenha o divisor do design. */}
              {items.length > 0 && (
                <div className="flex flex-col gap-6">
                  {items.map((item) => (
                    <NewsCardRow
                      item={item as NewsCardItem}
                      key={item['@id']}
                      showUnit={false}
                    />
                  ))}
                </div>
              )}

              {hasMore && (
                <div className="mt-2 flex flex-col items-center">
                  <MPFButton
                    /* `primary`, como o "Mostrar mais" gemeo da pagina de
                       noticia (`News/RelatedItems/index.tsx`): os dois fazem a
                       mesma coisa e sao o mesmo botao para o leitor. Aqui ele
                       tinha ficado em `tonal` (fundo claro `primary-container`
                       com texto escuro), destoando do padrao solido. */
                    variant="primary"
                    // L, sem largura fixa: o XL com 549 px estava fora da
                    // escala do Design System (AB#11387).
                    size="l"
                    className="text-base leading-base"
                    loading={loadingMore}
                    onClick={handleShowMore}
                  >
                    Mostrar mais
                  </MPFButton>
                  {/* Sentinela da rolagem infinita: fica abaixo do botão para
                      só cruzar a viewport quando a lista terminou. `h-px` não
                      é detalhe — elemento de altura zero é caso de borda do
                      IntersectionObserver e pode nunca ser dado como visível. */}
                  <div
                    ref={sentinelRef}
                    aria-hidden="true"
                    className="h-px w-full"
                  />
                </div>
              )}

              {items.length > 0 && (
                <p aria-live="polite" className="sr-only">
                  Mostrando{' '}
                  {items.length + highlights.length + destaquesFallback.length}{' '}
                  de {result?.items_total ?? 0} notícias.
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </NewsContext.Provider>
  );
};

export default AllNews;
